import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for RTL management
 * Provides consistent RTL state and utilities across the application
 */
export const useRTL = () => {
  const { i18n } = useTranslation();

  const isRTL = useMemo(() => {
    return ['dari', 'ps'].includes(i18n.language);
  }, [i18n.language]);

  const rtlProps = useMemo(() => {
    return {
      isRtl: isRTL,
      dir: isRTL ? 'rtl' : 'ltr',
    };
  }, [isRTL]);

  const getRTLProps = (component = 'textfield') => {
    if (!isRTL) return {};

    const baseProps = {
      isRtl: isRTL,
      dir: 'rtl',
    };

    switch (component) {
      case 'select':
        return {
          ...baseProps,
          MenuProps: {
            PaperProps: {
              sx: {
                direction: 'rtl',
                '& .MuiMenuItem-root': {
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                  direction: 'rtl',
                },
              },
            },
          },
        };
      case 'autocomplete':
        return {
          ...baseProps,
          renderOption: (props, option) => (
            <li {...props} style={{ textAlign: 'right', direction: 'rtl' }}>
              {option.label}
            </li>
          ),
        };
      case 'dialog':
        return {
          ...baseProps,
          sx: {
            direction: 'rtl',
            '& .MuiDialogTitle-root': {
              textAlign: 'right',
            },
            '& .MuiDialogContent-root': {
              textAlign: 'right',
            },
            '& .MuiDialogActions-root': {
              flexDirection: 'row-reverse',
            },
          },
        };
      default:
        return baseProps;
    }
  };

  return {
    isRTL,
    rtlProps,
    getRTLProps,
  };
};

export default useRTL;
